/**
 * Normalize Gobodo records for list view display.
 * List view columns use display names (name, title, status); Gobodo returns __c fields (Name__c, Subject__c).
 */

type RecordLike = Record<string, any>;

const CONTACT_MAP: Record<string, string> = {
  name: 'Name__c',
  title: 'Title__c',
  clientName: 'ClientName__c',
  email: 'Email__c',
  phone: 'Phone__c',
  mobile: 'MobilePhone__c',
  owner: 'Owner__c',
  lastContact: 'LastContact__c',
  status: 'Status__c',
  department: 'Department__c',
  accountId: 'AccountId__c',
};

const TASK_MAP: Record<string, string> = {
  title: 'Subject__c',
  status: 'Status__c',
  priority: 'Priority__c',
  dueDate: 'DueDate__c',
  type: 'Type__c',
  assignee: 'Owner__c',
  subject: 'Subject__c',
};

function pick(r: RecordLike, displayKey: string, gobodoKey: string): void {
  const val = r[gobodoKey];
  if (val !== undefined && val !== null) {
    r[displayKey] = val;
  }
}

/** Try display key and suffixed API names (__c and custom suffix) so Gobodo records show in list columns. */
function pickWithSuffix(r: RecordLike, displayKey: string, baseApiName: string): void {
  const suffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
  const val =
    r[displayKey] ??
    r[baseApiName] ??
    r[`${baseApiName}__c`] ??
    (suffix !== '__c' ? r[`${baseApiName.replace(/__c$/i, '')}${suffix}`] : undefined);
  if (val !== undefined && val !== null) {
    r[displayKey] = val;
  }
}

export function normalizeContactRecordForListView(r: RecordLike): RecordLike {
  const out = { ...r };
  (Object.entries(CONTACT_MAP) as [string, string][]).forEach(([display, gobodo]) => pick(out, display, gobodo));
  out.name = out.name ?? out.label ?? out.Name__c ?? r.id;
  return out;
}

export function normalizeTaskRecordForListView(r: RecordLike): RecordLike {
  const out = { ...r };
  (Object.entries(TASK_MAP) as [string, string][]).forEach(([display, gobodo]) => pick(out, display, gobodo));
  out.title = out.title ?? out.subject ?? out.Subject__c ?? out.label ?? r.id;
  return out;
}

export function normalizeRecordForListView(objectApiName: string, r: RecordLike): RecordLike & { id: string } {
  // Ensure id is always set (Gobodo records always have id)
  const baseId = r.id || r.Id || `generated-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const withId = { ...r, id: baseId } as RecordLike & { id: string };
  const norm = objectApiName.toLowerCase();
  if (norm === 'contact' || norm === 'contact__c') {
    const out = normalizeContactRecordForListView(withId);
    return { ...out, id: baseId } as RecordLike & { id: string };
  }
  if (norm === 'task' || norm === 'task__c') {
    const out = normalizeTaskRecordForListView(withId);
    return { ...out, id: baseId } as RecordLike & { id: string };
  }
  if (norm === 'lead' || norm === 'lead__c') {
    const out = { ...withId };
    pick(out, 'name', 'Name__c');
    pick(out, 'company', 'Company__c');
    pick(out, 'email', 'Email__c');
    pick(out, 'status', 'Status__c');
    out.name = out.name ?? out.label ?? out.Name__c ?? baseId;
    return out;
  }
  if (norm === 'event' || norm === 'event__c') {
    const out = { ...withId };
    pick(out, 'title', 'Subject__c');
    pick(out, 'status', 'Status__c');
    pick(out, 'eventType', 'EventType__c');
    out.title = out.title ?? out.Subject__c ?? out.label ?? baseId;
    return out;
  }
  if (norm === 'case' || norm === 'case__c') {
    const out = { ...withId };
    pick(out, 'caseNumber', 'CaseNumber__c');
    pick(out, 'subject', 'Subject__c');
    pick(out, 'status', 'Status__c');
    pick(out, 'priority', 'Priority__c');
    pick(out, 'owner', 'Owner__c');
    pick(out, 'assignedTo', 'Owner__c');
    pick(out, 'assignedTo', 'AssignedTo__c');
    pick(out, 'type', 'Type__c');
    pick(out, 'origin', 'Origin__c');
    pick(out, 'contactName', 'ContactName__c');
    pick(out, 'accountName', 'AccountName__c');
    pick(out, 'clientName', 'ClientName__c');
    pick(out, 'productName', 'ProductName__c');
    pick(out, 'description', 'Description__c');
    pick(out, 'resolution', 'Resolution__c');
    pick(out, 'closedDate', 'ClosedDate__c');
    out.clientName = out.clientName ?? out.accountName;
    out.lastModifiedDate = out.lastModifiedDate ?? out.lastModified;
    out.subject = out.subject ?? out.Subject__c ?? out.label ?? baseId;
    return out;
  }
  if (norm === 'client' || norm === 'client__c') {
    const out = { ...withId };
    pickWithSuffix(out, 'name', 'Name');
    pickWithSuffix(out, 'email', 'Email');
    pickWithSuffix(out, 'phone', 'Phone');
    pickWithSuffix(out, 'status', 'Status');
    pickWithSuffix(out, 'owner', 'Owner');
    pickWithSuffix(out, 'accountOwner', 'AccountOwner');
    pickWithSuffix(out, 'lastContact', 'LastContact');
    pickWithSuffix(out, 'industry', 'Industry');
    pickWithSuffix(out, 'createdDate', 'CreatedDate');
    pickWithSuffix(out, 'lastModifiedDate', 'LastModifiedDate');
    pickWithSuffix(out, 'accountName', 'AccountName');
    out.name = out.name ?? out.label ?? out.Name__c ?? (withId as RecordLike).Name__stg_c ?? (withId as RecordLike).Name__stg3_c ?? baseId;
    return out;
  }
  if (norm === 'opportunity' || norm === 'opportunity__c') {
    const out = { ...withId };
    pick(out, 'name', 'Name__c');
    pick(out, 'clientName', 'ClientName__c');
    pick(out, 'clientId', 'ClientId__c');
    pick(out, 'amount', 'Amount__c');
    pick(out, 'stage', 'Stage__c');
    pick(out, 'probability', 'Probability__c');
    pick(out, 'closeDate', 'CloseDate__c');
    pick(out, 'owner', 'Owner__c');
    pick(out, 'type', 'Type__c');
    pick(out, 'nextStep', 'NextStep__c');
    out.name = out.name ?? out.label ?? out.Name__c ?? baseId;
    return out;
  }
  if (norm === 'report' || norm === 'report__c') {
    const out = { ...withId };
    pick(out, 'name', 'Name__c');
    pick(out, 'objectName', 'ObjectName__c');
    pick(out, 'type', 'Type__c');
    pick(out, 'format', 'Format__c');
    pick(out, 'frequency', 'Frequency__c');
    pick(out, 'status', 'Status__c');
    pick(out, 'lastRunDate', 'LastRunDate__c');
    pick(out, 'createdBy', 'CreatedBy__c');
    pick(out, 'description', 'Description__c');
    pick(out, 'category', 'Category__c');
    pick(out, 'reportType', 'ReportType__c');
    out.name = out.name ?? out.label ?? out.Name__c ?? baseId;
    return out;
  }
  return withId;
}
